import { AnalyticsListener, UIAnalyticsEventInterface } from '@atlaskit/analytics-next';
import AtlassianSwitcher from '@atlaskit/atlassian-switcher';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import styled from 'styled-components';

import { getConfig } from '../config';
import { AnalyticsConsumerProps, withAnalytics } from '../hoc/withAnalytics';
import Store from '../store';
import DrawerStore, { Drawer as DrawerType } from '../store/drawerStore';

import { onDesktop, onMobile } from './mobile/responsive';

const { notificationsDrawerIframeUrl } = getConfig();

interface DrawerProps {
  isOpen: boolean;
  size?: string;
}

const whenOpen = (rule: string) => {
  return ({ isOpen }: DrawerProps) => {
    return isOpen ? rule : '';
  };
};

const whenClosed = (rule: string) => {
  return ({ isOpen }: DrawerProps) => {
    return isOpen ? '' : rule;
  };
};

const drawerTransitionSeconds = 0.25;

const Background = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  transition: background ${drawerTransitionSeconds}s;
  will-change: background;

  ${onDesktop(`
    // Ensure side nav is HIDDEN when drawer is opened
    z-index: 5;
  `)}

  ${onMobile(`
    // Ensure top nav is SHOWN when drawer is opened
    z-index: 2;
  `)}

  ${whenOpen(`
    background: rgba(50, 50, 50, 0.5);
  `)};
  ${whenClosed(`
    pointer-events: none;
  `)};
`;

const DrawerContainer = styled.div`
  position: fixed;
  pointer-events: all;
  top: 0;
  left: 0;
  width: ${(props: DrawerProps) => (props.size === 'small' ? '300px' : '500px')};
  height: 100vh;
  transform: translateX(-100%);
  will-change: transform;
  transition: transform ${drawerTransitionSeconds}s;
  background: white;
  display: flex;
  align-items: stretch;
  justify-content: center;

  // Spacing for desktop. To emulate the real AtlasKit drawer
  ${onDesktop(`
    padding-top: 24px;
    padding-left: 50px;
  `)}

  // Spacing for mobile. Use the full width because mobile doesn't have enough screen estate
  ${onMobile(`
    padding-top: 60px;
    padding-left: 10px;
    width: 100%;
  `)}

  ${whenOpen(`
    transform: translateX(0);
  `)};
`;

const SwitcherContainer = styled.div`
  width: 100%;
  overflow: auto;
`;

const NotificationsDrawerIframe = styled.iframe`
  border: none;
  flex-grow: 1;
`;

interface Props {
  drawerStore: DrawerStore;
}

interface State {
  // The currently closing drawer
  closingDrawer: DrawerType;
}

@observer
class Drawer extends React.Component<Props & InjectedIntlProps & AnalyticsConsumerProps, State> {
  state = {
    closingDrawer: DrawerType.None,
  };

  private pruneDrawerTimeout?: number;

  componentDidUpdate(prevProps: Props, prevState: State) {
    // Once the drawer has transitioned away, remove it from the DOM.
    if (prevState.closingDrawer === DrawerType.None && this.state.closingDrawer !== DrawerType.None) {
      this.pruneDrawerTimeout = window.setTimeout(this.pruneDrawer, drawerTransitionSeconds * 1000);
    }
  }

  componentWillUnmount() {
    if (this.pruneDrawerTimeout) {
      clearTimeout(this.pruneDrawerTimeout);
      this.pruneDrawerTimeout = undefined;
    }
  }

  render() {
    const { currentDrawer } = this.props.drawerStore;
    const { closingDrawer } = this.state;

    const { locale } = this.props.intl;

    // We want to render the drawer content if it's open, or it's transitioning away
    const renderNotificationsContent =
      currentDrawer === DrawerType.Notifications || closingDrawer === DrawerType.Notifications;
    const renderSwitcherContent = currentDrawer === DrawerType.Switcher || closingDrawer === DrawerType.Switcher;

    const isDrawerOpen = currentDrawer === DrawerType.Notifications || currentDrawer === DrawerType.Switcher;
    const size = renderSwitcherContent ? 'small' : 'medium';

    return (
      <Background
        data-test-id="drawer-background"
        isOpen={currentDrawer !== DrawerType.None}
        onClick={this.closeDrawer}
      >
        <DrawerContainer isOpen={isDrawerOpen} size={size}>
          {renderNotificationsContent && (
            <NotificationsDrawerIframe
              src={`${notificationsDrawerIframeUrl}?scope=user&product=uchi&locale=${locale}`}
            />
          )}
          {renderSwitcherContent && (
            <SwitcherContainer>
              <AnalyticsListener channel="navigation" onEvent={this.onSwitcherAnalyticsEvent}>
                <AtlassianSwitcher product="start" disableCustomLinks disableRecentContainers />
              </AnalyticsListener>
            </SwitcherContainer>
          )}
        </DrawerContainer>
      </Background>
    );
  }

  private closeDrawer = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.currentTarget !== e.target) {
      // ignore bubbled clicks
      return;
    }
    this.setState({
      closingDrawer: this.props.drawerStore.currentDrawer,
    });
    this.props.drawerStore.closeDrawer();
  };

  private onSwitcherAnalyticsEvent = (data: UIAnalyticsEventInterface) => {
    this.props.analytics.sendEventFromAnalyticsListener(data, 'navigation');
  };

  // Once the drawer has been transitioned off screen, don't render the iframe any more
  private pruneDrawer = (): void => {
    this.setState({
      closingDrawer: DrawerType.None,
    });
  };
}

export default inject(({ drawerStore }: Store) => ({
  drawerStore,
}))(injectIntl(withAnalytics(Drawer)));
